import { useNotificationContext } from '@modules/common/components/Notification/NotificationContext';
import { useGenerateEql } from '@modules/common/hooks';
import { useCommonStore } from '@modules/common/store/useCommonStore';
import { EqlType } from 'generated/graphql';
import { useCallback } from 'react';
import { QueryBuilderActionType } from '../store/queryBuilderActionType';
import { useQueryBuilderStore } from '../store/useQueryBuilderStore';
import { useGenerateUpdateFilters } from './useGenerateUpdateFilters';

export function useGenerateEqlAndApplyFilters() {
  const [generateEql, { isLoading }] = useGenerateEql();
  const dispatch = useQueryBuilderStore(({ dispatch }) => dispatch);
  const generateUpdateFilters = useGenerateUpdateFilters();
  const { showNotification } = useNotificationContext();

  const isQBModalClosed = useCommonStore(
    ({ isQBModalClosed }) => isQBModalClosed,
  );

  const generateEqlAndApplyFilters = useCallback(
    async (query: string) => {
      dispatch({
        type: QueryBuilderActionType.SET_METADATA,
        payload: {
          isLoading: true,
        },
      });

      const eql = await generateEql({
        eqlType: EqlType.EqlTypeFeed, // We are using it in both the feed and quantify page through QueryBuilder.
        query,
      });

      if (!eql?.properties.whereClause) {
        dispatch({
          type: QueryBuilderActionType.SET_METADATA,
          payload: {
            isLoading: false,
          },
        });

        showNotification({
          contentComponent: 'No relevant filters found',
          props: { severity: 'warning' },
        });

        return;
      }

      /**
       * Setting the loading state to true causes the loader component to render, which results in the unmounting of this component.
       * Once the component is unmounted, the callback will not have access to the state variables.
       * Therefore, using a storage variable is a viable workaround.
       */
      if (!isQBModalClosed) {
        await generateUpdateFilters(eql?.eql);
        return eql;
      }
    },
    [
      dispatch,
      generateEql,
      isQBModalClosed,
      showNotification,
      generateUpdateFilters,
    ],
  );

  return [generateEqlAndApplyFilters, { isLoading }] as const;
}
